import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Search, Plus, X, Edit2, Trash2, Video, Play, Dumbbell } from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from './AdminContext';

const defaultEquipmentTypes = ['barbell', 'dumbbell', 'cable', 'machine', 'bodyweight', 'other'];
const defaultMuscleGroups = ['Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps', 'Legs', 'Core', 'Glutes', 'Forearms', 'Full Body'];

export default function ExercisesTab({ refreshTrigger }) {
  const { adminFetch, API_URL } = useAdmin();
  const [exercises, setExercises] = useState([]);
  const [muscleGroups, setMuscleGroups] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [exerciseSearchQuery, setExerciseSearchQuery] = useState('');
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState('all');
  const [selectedEquipment, setSelectedEquipment] = useState('all');
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [exerciseForm, setExerciseForm] = useState({
    name: '', muscle_group: '', equipment_type: 'barbell', video_url: '', description: ''
  });

  const loadExercises = useCallback(async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/exercises`);
      if (response.ok) {
        const data = await response.json();
        setExercises(data.exercises || []);
        setMuscleGroups(data.muscle_groups || []);
        setEquipmentTypes(data.equipment_types || []);
      } else {
        toast.error(`Failed to load exercises: ${response.status}`);
      }
    } catch (error) {
      toast.error('Network error loading exercises');
    }
  }, [adminFetch, API_URL]);

  useEffect(() => { loadExercises(); }, [loadExercises, refreshTrigger]);

  const openExerciseModal = (exercise = null) => {
    if (exercise) {
      setEditingExercise(exercise);
      setExerciseForm({
        name: exercise.name || '', muscle_group: exercise.muscle_group || '',
        equipment_type: exercise.equipment_type || 'barbell',
        video_url: exercise.video_url || '', description: exercise.description || ''
      });
    } else {
      setEditingExercise(null);
      setExerciseForm({ name: '', muscle_group: '', equipment_type: 'barbell', video_url: '', description: '' });
    }
    setShowExerciseModal(true);
  };

  const closeExerciseModal = () => {
    setShowExerciseModal(false);
    setEditingExercise(null);
    setExerciseForm({ name: '', muscle_group: '', equipment_type: 'barbell', video_url: '', description: '' });
  };

  const saveExercise = async () => {
    if (!exerciseForm.name.trim() || !exerciseForm.muscle_group.trim()) {
      toast.error('Exercise name and muscle group are required');
      return;
    }
    try {
      const url = editingExercise
        ? `${API_URL}/api/v2/admin/exercises/${editingExercise.exercise_id}`
        : `${API_URL}/api/v2/admin/exercises`;
      const response = await fetch(url, {
        method: editingExercise ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(exerciseForm)
      });
      if (response.ok) {
        toast.success(editingExercise ? 'Exercise updated' : 'Exercise created');
        closeExerciseModal();
        loadExercises();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to save exercise');
      }
    } catch (error) {
      toast.error('Error saving exercise');
    }
  };

  const deleteExercise = async (exerciseId) => {
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/exercises/${exerciseId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Exercise deleted');
        loadExercises();
      } else {
        toast.error('Failed to delete exercise');
      }
    } catch (error) {
      toast.error('Error deleting exercise');
    }
  };

  const seedExercises = async () => {
    try {
      toast.info('Seeding exercises... This may take a moment.');
      const response = await fetch(`${API_URL}/api/v2/admin/exercises/seed`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(`${data.created || 0} exercises created, ${data.skipped || 0} skipped`);
        loadExercises();
      } else {
        toast.error(data.detail || `Failed to seed exercises (${response.status})`);
      }
    } catch (error) {
      toast.error(`Network error: ${error.message}`);
    }
  };

  const filteredExercises = exercises.filter(ex => {
    const matchesSearch = ex.name?.toLowerCase().includes(exerciseSearchQuery.toLowerCase()) ||
                          ex.muscle_group?.toLowerCase().includes(exerciseSearchQuery.toLowerCase());
    const matchesMuscle = selectedMuscleGroup === 'all' || ex.muscle_group === selectedMuscleGroup;
    const matchesEquipment = selectedEquipment === 'all' || ex.equipment_type === selectedEquipment;
    return matchesSearch && matchesMuscle && matchesEquipment;
  });

  return (
    <div className="space-y-4" data-testid="exercises-tab">
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full lg:w-auto">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search exercises..." value={exerciseSearchQuery} onChange={(e) => setExerciseSearchQuery(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-600 text-white" data-testid="exercise-search-input" />
          </div>
          <select value={selectedMuscleGroup} onChange={(e) => setSelectedMuscleGroup(e.target.value)}
            className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm" data-testid="muscle-group-filter">
            <option value="all">All Muscle Groups</option>
            {[...new Set([...defaultMuscleGroups, ...muscleGroups])].sort().map(group => (
              <option key={group} value={group}>{group}</option>
            ))}
          </select>
          <select value={selectedEquipment} onChange={(e) => setSelectedEquipment(e.target.value)}
            className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm" data-testid="equipment-filter">
            <option value="all">All Equipment</option>
            {[...new Set([...defaultEquipmentTypes, ...equipmentTypes])].sort().map(type => (
              <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          {exercises.length === 0 && (
            <Button onClick={seedExercises} className="bg-green-600 hover:bg-green-500" data-testid="seed-exercises-btn">
              <Dumbbell className="h-4 w-4 mr-2" />Load Default Exercises
            </Button>
          )}
          <Button onClick={() => openExerciseModal()} className="bg-orange-500 hover:bg-orange-400" data-testid="add-exercise-btn">
            <Plus className="h-4 w-4 mr-2" />Add Exercise
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredExercises.map(exercise => (
          <Card key={exercise.exercise_id} className="bg-slate-800 border-slate-700" data-testid={`exercise-card-${exercise.exercise_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-white font-medium text-lg">{exercise.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">{exercise.muscle_group}</span>
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full capitalize">{exercise.equipment_type}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openExerciseModal(exercise)}
                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20" data-testid={`edit-exercise-${exercise.exercise_id}`}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteExercise(exercise.exercise_id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/20" data-testid={`delete-exercise-${exercise.exercise_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {exercise.description && <p className="text-slate-400 text-sm mb-3 line-clamp-2">{exercise.description}</p>}
              {exercise.video_url && (
                <a href={exercise.video_url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-orange-400 hover:text-orange-300 text-sm" data-testid={`video-link-${exercise.exercise_id}`}>
                  <Video className="h-4 w-4" /><span>Watch Video</span><Play className="h-3 w-3" />
                </a>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredExercises.length === 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="py-12 text-center">
            <Dumbbell className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">
              {exercises.length === 0 ? 'No exercises yet. Click "Add Exercise" to create one.' : 'No exercises match your filters.'}
            </p>
          </CardContent>
        </Card>
      )}

      <p className="text-slate-500 text-sm">Showing {filteredExercises.length} of {exercises.length} exercises</p>

      {showExerciseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-slate-800 border-slate-700 w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Dumbbell className="h-5 w-5 text-orange-400" />
                {editingExercise ? 'Edit Exercise' : 'Add New Exercise'}
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={closeExerciseModal} className="text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-300">Exercise Name *</Label>
                <Input value={exerciseForm.name} onChange={(e) => setExerciseForm({...exerciseForm, name: e.target.value})}
                  placeholder="e.g., Bench Press" className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="exercise-name-input" />
              </div>
              <div>
                <Label className="text-slate-300">Muscle Group *</Label>
                <div className="flex gap-2 mt-1">
                  <select value={exerciseForm.muscle_group} onChange={(e) => setExerciseForm({...exerciseForm, muscle_group: e.target.value})}
                    className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" data-testid="exercise-muscle-group-select">
                    <option value="">Select muscle group</option>
                    {[...new Set([...defaultMuscleGroups, ...muscleGroups])].sort().map(group => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </select>
                  <Input value={exerciseForm.muscle_group} onChange={(e) => setExerciseForm({...exerciseForm, muscle_group: e.target.value})}
                    placeholder="Or type custom" className="w-32 bg-slate-700 border-slate-600 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Equipment Type *</Label>
                <select value={exerciseForm.equipment_type} onChange={(e) => setExerciseForm({...exerciseForm, equipment_type: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white mt-1" data-testid="exercise-equipment-select">
                  {defaultEquipmentTypes.map(type => (
                    <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-slate-300">Video URL</Label>
                <Input value={exerciseForm.video_url} onChange={(e) => setExerciseForm({...exerciseForm, video_url: e.target.value})}
                  placeholder="https://youtube.com/watch?v=..." className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="exercise-video-input" />
                <p className="text-slate-500 text-xs mt-1">YouTube, Vimeo, or any video link</p>
              </div>
              <div>
                <Label className="text-slate-300">Description</Label>
                <textarea value={exerciseForm.description} onChange={(e) => setExerciseForm({...exerciseForm, description: e.target.value})}
                  placeholder="Brief description or form tips..." rows={3}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="exercise-description-input" />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={closeExerciseModal} className="border-slate-600 text-slate-300">Cancel</Button>
                <Button onClick={saveExercise} className="bg-orange-500 hover:bg-orange-400" data-testid="save-exercise-btn">
                  {editingExercise ? 'Update Exercise' : 'Create Exercise'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
